import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config/api.config";
import "./BannerPopup.css";
import CompetitionTerms from "./CompetitionTerms";
import { getBannerDestination } from "./bannerUtils";
import { useAuth } from "../../context/AuthContext";
import { SHOW_BANNER_TO_LOGGED_IN_USERS } from "./bannerPopup.config";

const SHOW_DELAY = 2000;
const REOPEN_DELAY = 2000;
const hiddenRoutes = ["/signup", "/signin", "/login", "/seller", "/admin"];


const BannerPopup = () => {
  const { isLoggedIn, loading, token } = useAuth();
  const [hasStoredToken, setHasStoredToken] = useState(() => Boolean(localStorage.getItem("token")));
  const [banner, setBanner] = useState(null);
  const [open, setOpen] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [reopenRequested, setReopenRequested] = useState(false);
  const cardRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const shouldHidePopup = hiddenRoutes.some((route) => location.pathname.startsWith(route)) ||
    (!SHOW_BANNER_TO_LOGGED_IN_USERS && (loading || isLoggedIn || hasStoredToken));

  useEffect(() => {
    const syncSession = () => setHasStoredToken(Boolean(localStorage.getItem("token")));
    syncSession();
    window.addEventListener("storage", syncSession);
    window.addEventListener("userLoggedIn", syncSession);
    window.addEventListener("userLoggedOut", syncSession);
    return () => {
      window.removeEventListener("storage", syncSession);
      window.removeEventListener("userLoggedIn", syncSession);
      window.removeEventListener("userLoggedOut", syncSession);
    };
  }, [isLoggedIn, token, location.pathname]);

  useEffect(() => {
    setOpen(false);
    setReopenRequested(false);
    setBanner(null);
    setShowTerms(false);
    if (shouldHidePopup) return undefined;
    const controller = new AbortController();
    let active = true;
    let timer;
    const loadBanner = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}${config.endpoints.publicBanners}`, {
          params: { position: "home" },
          signal: controller.signal,
        });
        if (!active || response.data.status !== "success") return;
        // The API sorts active banners by order, then newest first.
        const selected = response.data.data?.banners?.find((item) => item.image);
        if (!selected) return;
        setBanner(selected);
        timer = setTimeout(() => setOpen(true), SHOW_DELAY);
      } catch {
        // An unavailable promotion must not interrupt shopping.
      }
    };
    loadBanner();
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [location.pathname, shouldHidePopup]);

  useEffect(() => {
    if (!reopenRequested || open || shouldHidePopup || !banner) return undefined;
    const timer = setTimeout(() => {
      setReopenRequested(false);
      setOpen(true);
    }, REOPEN_DELAY);
    return () => clearTimeout(timer);
  }, [reopenRequested, open, shouldHidePopup, banner]);

  const handleClose = useCallback(() => {
    if (showTerms) {
      setShowTerms(false);
      return;
    }
    setOpen(false);
    setReopenRequested(true);
  }, [showTerms]);
  const link = getBannerDestination(banner);
  const handleFollowLink = (event) => {
    if (!link) return;
    setOpen(false);
    setReopenRequested(false);
    if (link.startsWith("/")) {
      event.preventDefault();
      navigate(link);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cardRef.current?.querySelector("button")?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") handleClose();
      if (event.key !== "Tab") return;
      const elements = cardRef.current?.querySelectorAll("button, a[href]");
      if (!elements?.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, [open, handleClose]);

  if (!open || shouldHidePopup || !banner) return null;

  const image = <img src={banner.image} alt={banner.title} onError={() => { setOpen(false); setReopenRequested(false); }} />;

  return (
    <div className="gp-overlay" onClick={handleClose}>
      <div className={showTerms ? "gp-card gp-card-terms" : "gp-card"} ref={cardRef} onClick={(event) => event.stopPropagation()}
        role="dialog" aria-modal="true" aria-labelledby={showTerms ? "competition-terms-title" : "promotion-title"}>
        <button type="button" className="gp-close" onClick={handleClose} aria-label={showTerms ? "Close terms and return to banner" : "Close popup"}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
          </svg>
        </button>
        {showTerms ? (
          <CompetitionTerms competitionName={banner.competitionName || banner.title} onBack={() => setShowTerms(false)} />
        ) : (
          <>
        {link ? (
          <a className="gp-banner" href={link} onClick={handleFollowLink}>{image}</a>
        ) : (
          <div className="gp-banner">{image}</div>
        )}
        <div className="gp-body">
          <h2 className="gp-title" id="promotion-title">{banner.title}</h2>
          {(banner.statusLabel || banner.secondaryLabel) && (
            <div className="gp-chips">
              {banner.statusLabel && <div className="gp-status"><span className="gp-dot" />{banner.statusLabel}</div>}
              {banner.secondaryLabel && <div className="gp-limited">{banner.secondaryLabel}</div>}
            </div>
          )}
          {banner.subtitle && <p className="gp-sub">{banner.subtitle}</p>}
          {link && banner.buttonText && (
            <a className="gp-cta" href={link} onClick={handleFollowLink}>
              <span>{banner.buttonText}</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          )}
          <button type="button" className="gp-skip gp-terms-trigger" onClick={() => setShowTerms(true)}>Terms &amp; Conditions</button>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BannerPopup;
